import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { LOG_CONFIGS, selectStyles } from "../Constants";
import ReactQuill from "react-quill-new";
import LogComment from "./LogComment";
import { useUser } from "../../contexts/UserContext";
import { useAdminContext } from "../../contexts/AdminContext";
import generateLogsPDF from "./GenerateLogPdf";
import PdfPreviewModal from "../PreviewPdfModal";
import "react-quill-new/dist/quill.snow.css";
import { formatOldUrl } from "../../utils/cloudApi";

const LogsForm = ({
  setNavbarButtons,
  logType,
  logData = null,
  dropdownLists = {},
  onSave,
  onCancel,
  onAddToDropdown,
  onNewLog,
  templates,
  isLoading = false,
}) => {
  const { userData, invoiceClients } = useUser();
  const { isViewingOtherUser, adminData } = useAdminContext();
  const location = useLocation();
  const config = LOG_CONFIGS[logType];
  const [formData, setFormData] = useState(() => {
    if (!config) return {};

    const initialData = {};
    Object.keys(config.defaultRow || {}).forEach((key) => {
      initialData[key] = config.defaultRow[key] || "";
    });

    return {
      ...initialData,
      createdAt: new Date().toISOString(),
      templateRef: null,
    };
  });
  const [errors, setErrors] = useState({});
  const [headerImageDataUrl, setHeaderImageDataUrl] = useState(null);
  const [footerImageDataUrl, setFooterImageDataUrl] = useState(null);
  const [headerImageDims, setHeaderImageDims] = useState({
    width: 0,
    height: 0,
  });

  const [footerImageDims, setFooterImageDims] = useState({
    width: 0,
    height: 0,
  });
  const [recordType, setRecordType] = useState(logData?.recordType || "log");
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ listKey: "", label: "" });
  const [newValue, setNewValue] = useState("");
  const [template, setTemplate] = useState({});
  const [isAddressedToExpanded, setIsAddressedToExpanded] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPreviewLog, setShowPreviewLog] = useState(false);
  const currentInvoiceClients = isViewingOtherUser
    ? adminData?.userClients
    : invoiceClients;

  const clientOptions = useMemo(() => {
    if (!currentInvoiceClients) return [{ value: "", label: "New Client" }];

    return [
      { value: "", label: "New Client" },
      ...currentInvoiceClients.map((client) => ({
        value: client.id,
        label: `${client.name}`,
      })),
    ];
  }, [currentInvoiceClients]);

  const quillModules = {
    toolbar: [
      [{ header: [3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
    ],
    keyboard: {
      bindings: {
        tab: {
          key: 9,
          handler: function (range) {
            this.quill.insertEmbed(
              range.index,
              "text",
              "\u00A0\u00A0\u00A0\u00A0"
            );
            return false;
          },
        },
      },
    },
  };
  // Initialize form data
  useEffect(() => {
    if (!config) return;

    const baseData = {
      ...(config.defaultRow || {}),
      createdAt: new Date().toISOString(),
      templateRef: null,
    };

    if (logData) {
      // Edit mode - Start with logData and ensure defaults for missing fields
      const normalizedLogData = { ...logData };

      // Set recordType from logData
      setRecordType(normalizedLogData.recordType || "log");

      // Then ensure default values for any missing fields from config
      Object.keys(config.defaultRow || {}).forEach((key) => {
        if (normalizedLogData[key] === undefined) {
          // Handle nested objects like 'to'
          if (
            typeof config.defaultRow[key] === "object" &&
            config.defaultRow[key] !== null &&
            !Array.isArray(config.defaultRow[key])
          ) {
            normalizedLogData[key] = { ...config.defaultRow[key] };
          } else {
            normalizedLogData[key] = config.defaultRow[key];
          }
        }
      });

      if (
        normalizedLogData.preparedBy &&
        !Array.isArray(normalizedLogData.preparedBy)
      ) {
        normalizedLogData.preparedBy = [normalizedLogData.preparedBy];
      }

      const newFormData = {
        ...normalizedLogData,
        id: logData.id,
        firebaseId: logData.firebaseId,
        createdAt: logData.createdAt,
        templateRef: logData.templateRef || null,
      };
      setFormData(newFormData);

      if (newFormData.templateRef) {
        const matchedTemplate = templates.find(
          (t) => t.id === newFormData.templateRef
        );

        setTemplate(matchedTemplate);
        setHeaderImageDataUrl(formatOldUrl(matchedTemplate?.logo) || null);
        setFooterImageDataUrl(formatOldUrl(matchedTemplate?.footer || null));
      }
    } else {
      // Create mode
      const locationState = location.state;

      if (locationState?.template) {
        const template = locationState.template;

        // Set recordType from template
        setRecordType(template.recordType || "log");

        // Pre-fill with template data, ensuring all values are defined
        const templateData = {};
        Object.keys(config.defaultRow || {}).forEach((key) => {
          templateData[key] =
            locationState.template.data?.[key] || baseData[key] || "";
        });

        setFormData({
          ...baseData,
          ...templateData,
          recordType: template.recordType || "log",
          templateRef: locationState.template.id || null,
        });
        setTemplate(locationState.template);

        // console.log(locationState);
        setHeaderImageDataUrl(
          formatOldUrl(locationState?.template?.logo) || null
        );
        setFooterImageDataUrl(
          formatOldUrl(locationState?.template?.footer || null)
        );
      } else {
        // Use default - ensure all values are defined
        const defaultData = {};
        Object.keys(config.defaultRow || {}).forEach((key) => {
          defaultData[key] = baseData[key] || "";
        });

        setRecordType("log");

        setFormData({
          ...baseData,
          ...defaultData,
          recordType: "log",
        });

        setHeaderImageDataUrl(null);
        setFooterImageDataUrl(null);
      }
    }
  }, [logData, config, location.state, templates]);

  // Auto-update or clear dateCompleted based on status
  useEffect(() => {
    if (formData.status === "Completed") {
      // Set to today if not already set
      if (!formData.dateCompleted) {
        const today = new Date().toISOString().split("T")[0];
        setFormData((prev) => ({
          ...prev,
          dateCompleted: today,
        }));
      }
    } else {
      // Clear date if status is not Completed
      if (formData.dateCompleted) {
        setFormData((prev) => ({
          ...prev,
          dateCompleted: "",
        }));
      }
    }
  }, [formData.status]);

  const handlePdf = useCallback(
    async (log, action) => {
      try {
        const matchedTemplate = templates.find((t) => t.id === log.templateRef);

        const logTypeList = {
          "secretarial-records": "secretary",
          "exec-admin": "exec",
          "process-server-logs": "server",
          "case-brief": "casebrief",
        };

        const curLogType = logTypeList[logType];
        const pdfUrl = await generateLogsPDF(
          log,
          matchedTemplate,
          action,
          curLogType
        );

        if (action === "preview") {
          setPdfUrl(pdfUrl);
          setShowPreviewLog(true);
        }

        if (action === "print" && pdfUrl) {
          const blobUrl = pdfUrl;
          const iframe = document.createElement("iframe");
          iframe.style.visibility = "hidden";
          iframe.style.position = "fixed";
          iframe.style.right = "0";
          iframe.style.bottom = "0";
          iframe.src = blobUrl;
          document.body.appendChild(iframe);
          iframe.onload = () => {
            iframe.contentWindow?.print();
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
              document.body.removeChild(iframe);
            }, 60000);
          };
        }
      } catch (error) {
        console.error(error);
      }
    },
    [logType, templates]
  );

  const closePreview = () => {
    setShowPreviewLog(false);
  };

  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const sanitizedFormData = {
        ...formData,
        ...(formData.body1 && {
          body1: formData.body1
            .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
            .replace(/ {2}/g, "&nbsp;&nbsp;"),
        }),
      };

      onSave(sanitizedFormData);
    },
    [formData]
  );

  // --- Memoize Button Config ---
  const buttonConfig = useMemo(() => {
    const cfg = [
      {
        onClick: onCancel,
        icon: "arrow_back",
        label: "Go back",
        className: "text-xs",
      },
      ...(logData && recordType !== "nte"
        ? [
            {
              onClick: () => onNewLog(formData.templateRef),
              icon: "note_add",
              label: `New Log`,
              className: "text-xs",
            },
          ]
        : []),
      {
        onClick: () => handlePdf(formData, "preview"),
        icon: "visibility",
        label: `Preview Log`,
        className: "text-xs",
      },
      {
        onClick: () => handlePdf(formData, "print"),
        icon: "print",
        label: "Print",
        className: "text-xs",
      },
      {
        onClick: () => handlePdf(formData, "download"),
        icon: "cloud_download",
        label: "Download",
        className: "text-xs",
      },
      {
        onClick: () => handleSubmit(),
        icon: "save",
        label: `${logData ? "Update" : "Create"} Log`,
        className: "text-xs",
      },
    ];
    return cfg;
  }, [handleSubmit, onNewLog, formData, logData, onCancel]);

  useEffect(() => {
    setNavbarButtons(buttonConfig);
    return () => setNavbarButtons([]);
  }, [setNavbarButtons, buttonConfig]);

  // Move useMemo hooks BEFORE the safety check
  const selectedClientOption = useMemo(() => {
    if (!formData.to?.name || formData.to.name.trim() === "") {
      return { value: "", label: "New Client" };
    }

    const foundClient = currentInvoiceClients?.find(
      (client) =>
        client.name.trim().toLowerCase() ===
        formData.to.name.trim().toLowerCase()
    );

    const result = foundClient
      ? {
          value: foundClient.id,
          label: `${foundClient.name}`,
        }
      : {
          value: "custom",
          label: formData.to.name,
        };
    return result;
  }, [currentInvoiceClients, formData?.to]);

  // Add this helper function after your other handler functions
  const handlePreparedByChange = useCallback((index, field, value) => {
    setFormData((prev) => {
      const preparedByArray = Array.isArray(prev.preparedBy)
        ? [...prev.preparedBy]
        : [];

      // Ensure the index exists
      if (!preparedByArray[index]) {
        preparedByArray[index] = { name: "", position: "", company: "" };
      }

      preparedByArray[index] = {
        ...preparedByArray[index],
        [field]: value,
      };

      return {
        ...prev,
        preparedBy: preparedByArray,
      };
    });
  }, []);

  const addPreparedBy = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      preparedBy: [
        ...(Array.isArray(prev.preparedBy) ? prev.preparedBy : []),
        { name: "", position: "", company: "" },
      ],
    }));
  }, []);

  const removePreparedBy = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      preparedBy: (prev.preparedBy || []).filter((_, i) => i !== index),
    }));
  }, []);

  // Safety check for missing config - must be after all hooks
  if (!config) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white/10 rounded-lg shadow-lg border dark:border-gray-600 border-gray-200 p-6">
          <div className="text-center text-red-500">
            <span className="material-symbols-outlined text-4xl">error</span>
            <p className="mt-2">Invalid log type: {logType}</p>
            <p className="text-sm text-alt-color mt-1">
              Please check your configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleClientSelect = (selectedOption) => {
    if (!selectedOption || selectedOption.value === "") {
      // Clear all fields when "New Client" is selected
      setFormData((prev) => ({
        ...prev,
        to: {
          name: "",
          tinNo: "",
          address: "",
          contact: "",
          rep: "",
          email: "",
        },
      }));
      setIsAddressedToExpanded(true); // Auto-expand for new client input
      return;
    }

    // Find the selected client
    const selectedClient = currentInvoiceClients.find(
      (client) => client.id === selectedOption.value
    );

    if (selectedClient) {
      setFormData((prev) => ({
        ...prev,
        to: {
          name: selectedClient.name || "",
          tinNo: selectedClient.tinNo || "",
          address: selectedClient.address || "",
          contact: selectedClient.contact || "",
          rep: selectedClient.rep || "",
          email: selectedClient.email || "",
        },
      }));
      setIsAddressedToExpanded(false); // Auto-collapse when existing client is selected
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleChange = (e, field, subfield) => {
    if (subfield) {
      setFormData((prev) => ({
        ...prev,
        [field]: { ...prev[field], [subfield]: e.target.value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.templateRef) {
      return;
    }

    // Validation for Notice to Explain
    if (recordType === "nte") {
      if (!formData.nteDate || formData.nteDate.trim() === "") {
        newErrors.nteDate = "Date is required";
      }
      if (!formData.letterOpening || formData.letterOpening.trim() === "") {
        newErrors.letterOpening = "Letter opening is required";
      }
      if (
        !formData.body1 ||
        formData.body1.trim() === "" ||
        formData.body1 === "<p><br></p>"
      ) {
        newErrors.body1 = "Body Part 1 is required";
      }
      // if (
      //   !formData.body2 ||
      //   formData.body2.trim() === "" ||
      //   formData.body2 === "<p><br></p>"
      // ) {
      //   newErrors.body2 = "Body Part 2 is required";
      // }
      if (
        !formData.closingRemarks?.name ||
        formData.closingRemarks.name.trim() === ""
      ) {
        newErrors.closingRemarksName = "Closing remarks name is required";
      }
      if (
        !formData.closingRemarks?.position ||
        formData.closingRemarks.position.trim() === ""
      ) {
        newErrors.closingRemarksPosition =
          "Closing remarks position is required";
      }
      if (
        !formData.closingRemarks?.company ||
        formData.closingRemarks.company.trim() === ""
      ) {
        newErrors.closingRemarksCompany = "Closing remarks company is required";
      }
    }
    // Validation for regular logs
    else {
      // Add validation rules based on log type
      if (logType === "secretarial-records") {
        if (!formData.task || formData.task.trim() === "") {
          newErrors.task = "Task is required";
        }
      } else if (
        logType === "process-server-logs" ||
        logType === "exec-admin"
      ) {
        if (!formData.priority || formData.priority.trim() === "") {
          newErrors.priority = "Priority is required";
        }
        if (!formData.task || formData.task.trim() === "") {
          newErrors.task = "Task is required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDropdownOptions = (listKey, placeholder = "Select...") => [
    { value: "", label: placeholder },
    ...(dropdownLists[listKey] || []).map((item) => ({
      value: item,
      label: item,
    })),
    { value: "__add_new__", label: "+ Add New" },
  ];

  const openModal = (listKey, label) => {
    setModalConfig({ listKey, label });
    setShowModal(true);
    setNewValue("");
  };

  const addToList = () => {
    const { listKey, label } = modalConfig;
    if (
      newValue.trim() &&
      !(dropdownLists[listKey] || []).includes(newValue.trim())
    ) {
      onAddToDropdown(listKey, newValue.trim());

      const fieldMap = {
        secretaryList: "secretary",
        processedByList: "processedBy",
        assistedList: "assistedTo",
        agencyList: "agency",
      };

      const fieldName = fieldMap[listKey];
      if (fieldName) {
        handleInputChange(fieldName, newValue.trim());
      }

      setNewValue("");
      setShowModal(false);
    }
  };

  return (
    <div className="p-6 flex flex-col items-center gap-4">
      {/* Modal for adding new dropdown values */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-tier-primary dark:bg-tier-primary-dark border border-gray-400 dark:border-gray-600 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <h3 className="text-xl font-semibold mb-4 text-color">
              Manage {modalConfig.label}
            </h3>

            {/* Add New Section */}
            <div className="mb-4 pb-4 border-b border-gray-300 dark:border-gray-600">
              <label className="block text-xs text-color ">Add New Item</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && addToList()}
                  className="custom-input flex-1"
                  placeholder={`Enter ${modalConfig.label.toLowerCase()}...`}
                  autoFocus
                />
                <button
                  onClick={addToList}
                  className="btn-action px-4"
                  disabled={!newValue.trim()}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Current List Section */}
            <div className="flex-1 overflow-y-auto">
              <label className="block text-xs text-color ">
                Current Items (
                {(dropdownLists[modalConfig.listKey] || []).length})
              </label>

              {(dropdownLists[modalConfig.listKey] || []).length === 0 ? (
                <div className="text-center py-8 text-alt-color">
                  <span className="material-symbols-outlined text-4xl opacity-50">
                    inbox
                  </span>
                  <p className="mt-2">No items yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(dropdownLists[modalConfig.listKey] || []).map(
                    (item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-2 bg-white/5 rounded border border-gray-300 dark:border-gray-600 hover:bg-white/10 transition-colors"
                      >
                        <span className="flex-1 text-color">{item}</span>
                        <button
                          onClick={() => {
                            const newItem = prompt("Edit item:", item);
                            if (newItem && newItem.trim() && newItem !== item) {
                              const updatedList = [
                                ...dropdownLists[modalConfig.listKey],
                              ];
                              updatedList[index] = newItem.trim();
                              onAddToDropdown(
                                modalConfig.listKey,
                                updatedList,
                                true
                              );
                            }
                          }}
                          className="p-1 hover:bg-blue-500/20 rounded transition-colors"
                          title="Edit"
                        >
                          <span className="material-symbols-outlined text-sm text-blue-500">
                            edit
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${item}"?`)) {
                              const updatedList = dropdownLists[
                                modalConfig.listKey
                              ].filter((_, i) => i !== index);
                              onAddToDropdown(
                                modalConfig.listKey,
                                updatedList,
                                true
                              );
                            }
                          }}
                          className="p-1 hover:bg-red-500/20 rounded transition-colors"
                          title="Delete"
                        >
                          <span className="material-symbols-outlined text-sm text-red-500">
                            delete
                          </span>
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNewValue("");
                }}
                className="btn-action-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="flex flex-col gap-2 w-full max-w-6xl min-w-[768px]">
        <div className="form-section max-w-6xl w-full">
          {/* Header */}
          {/* <div className="mb-6 flex flex-row w-full items-center gap-1">
            {logType === "secretarial-records" && (
              <span className="uppercase text-sm">
                {!template.recordType || template.recordType === "log"
                  ? "record"
                  : "Notice To Explain"}
                {" | "}
              </span>
            )}

            <h2 className="text-sm italic font-bold text-tier-secondary">
              {template?.name}
            </h2>
          </div> */}

          <div className="border-b-2 border-tier-secondary my-4"></div>

          <div className="mb-6 text-center">
            <label htmlFor="header" className="block text-xs">
              Document Title
            </label>
            <input
              id="header"
              type="text"
              value={
                recordType === "nte" ? "Notice To Explain" : template?.name
              }
              readOnly
              className="text-2xl px-4 py-2 font-bold text-color bg-white/20 rounded-lg text-center w-full focus:outline-none"
              placeholder="Enter Header"
            />
          </div>

          {/* Header Image */}
          <div className="m-4 flex flex-col items-center justify-center">
            {headerImageDataUrl && (
              <>
                <span className="block text-xs">Header Image</span>
                <img
                  src={formatOldUrl(headerImageDataUrl) || null}
                  alt="Header"
                  className="border max-h-32 h-auto max-w-lg border-gray-300 dark:border-gray-600"
                  onLoad={(e) =>
                    setHeaderImageDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {headerImageDims.width}px ×{" "}
                  {headerImageDims.height}px
                </p>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="m-4 flex flex-col items-center justify-center">
            {footerImageDataUrl && (
              <>
                <span className="block text-xs">Footer Image</span>
                <img
                  src={formatOldUrl(footerImageDataUrl) || null}
                  alt="Footer"
                  className="border max-h-32 h-auto max-w-lg border-gray-300 dark:border-gray-600"
                  onLoad={(e) =>
                    setFooterImageDims({
                      width: e.currentTarget.naturalWidth,
                      height: e.currentTarget.naturalHeight,
                    })
                  }
                />
                <p className="mt-1 text-xs text-center text-gray-200">
                  Dimensions: {footerImageDims.width}px ×{" "}
                  {footerImageDims.height}px
                </p>
              </>
            )}
          </div>

          <div className="border-b-2 border-tier-secondary pb-4"></div>

          <form onSubmit={handleSubmit}>
            {/* Addressed To */}
            <div className="m-4">
              <h2 className="text-xl font-bold mb-4">Addressed To</h2>

              {/* Client Selection Dropdown - Always visible */}
              <div className="mb-4">
                <label htmlFor="client-select" className="block text-sm ">
                  Select Client
                </label>

                <Select
                  value={selectedClientOption}
                  onChange={handleClientSelect}
                  options={clientOptions}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isSearchable={true}
                  placeholder="Select client..."
                  noOptionsMessage={() => "No clients found"}
                  loadingMessage={() => "Loading clients..."}
                />
              </div>

              {/* Collapsible Details Section */}
              <button
                type="button"
                onClick={() => setIsAddressedToExpanded(!isAddressedToExpanded)}
                className="flex items-center gap-2 text-sm text-tier-secondary hover:text-tier-secondary/80 transition-colors mb-2"
              >
                <span className="material-symbols-outlined text-base">
                  {isAddressedToExpanded ? "expand_less" : "expand_more"}
                </span>
                {isAddressedToExpanded ? "Hide" : "Show"} Details
              </button>

              {/* Collapsible Content */}
              {isAddressedToExpanded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 border-t border-gray-300 dark:border-gray-600 pt-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="to-name" className="block text-sm">
                      Name
                    </label>
                    <input
                      id="to-name"
                      type="text"
                      value={formData?.to?.name || ""}
                      onChange={(e) => handleChange(e, "to", "name")}
                      className="w-full custom-input"
                      placeholder="Enter name"
                    />
                  </div>

                  <div>
                    <label htmlFor="to-tinNo" className="block text-sm">
                      TIN
                    </label>
                    <input
                      id="to-tinNo"
                      type="text"
                      value={formData?.to?.tinNo || ""}
                      onChange={(e) => handleChange(e, "to", "tinNo")}
                      className="w-full custom-input"
                      placeholder="Enter TIN"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="to-representative"
                      className="block text-sm"
                    >
                      Representative
                    </label>
                    <input
                      id="to-representative"
                      type="text"
                      value={formData?.to?.rep || ""}
                      onChange={(e) => handleChange(e, "to", "rep")}
                      className="w-full custom-input"
                      placeholder="Enter representative name"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="to-address" className="block text-sm">
                      Address
                    </label>
                    <input
                      id="to-address"
                      type="text"
                      value={formData?.to?.address || ""}
                      onChange={(e) => handleChange(e, "to", "address")}
                      className="w-full custom-input"
                      placeholder="Enter address"
                    />
                  </div>

                  <div>
                    <label htmlFor="to-contact" className="block text-sm">
                      Contact
                    </label>
                    <input
                      id="to-contact"
                      type="text"
                      value={formData?.to?.contact || ""}
                      onChange={(e) => handleChange(e, "to", "contact")}
                      className="w-full custom-input"
                      placeholder="Enter contact details"
                    />
                  </div>

                  <div>
                    <label htmlFor="to-email" className="block text-sm">
                      Email
                    </label>
                    <input
                      id="to-email"
                      type="text"
                      value={formData?.to?.email || ""}
                      onChange={(e) => handleChange(e, "to", "email")}
                      className="w-full custom-input"
                      placeholder="Enter email address"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="border-b-2 border-tier-secondary pb-4"></div>
            {recordType === "nte" ? (
              // NTE-specific fields
              <>
                <div className="col-span-3 pt-4">
                  <label htmlFor="subject" className="block text-xs">
                    Subject/InRe
                  </label>
                  <input
                    id="subject"
                    type="text"
                    value={formData.subject || ""}
                    onChange={(e) => handleChange(e, "subject")}
                    className="w-full custom-input px-4 py-2 border rounded"
                    placeholder="Enter Subject or InRe"
                  />
                </div>

                <div className="flex flex-row gap-2 pt-3">
                  {/* Letter Opening */}
                  <div className="mb-4 flex-1">
                    <label className="block text-xs ">
                      Letter Opening <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.letterOpening || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          letterOpening: e.target.value,
                        }))
                      }
                      placeholder="e.g., Dear Mr. Smith"
                      className="custom-input"
                    />
                  </div>
                  {/* Date Field */}
                  <div className="mb-4">
                    <label className="block text-xs ">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.nteDate || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          nteDate: e.target.value,
                        }))
                      }
                      className="custom-input"
                    />
                  </div>
                </div>

                {/* Body 1 - Rich Text */}
                <div className="mb-4">
                  <label className="block text-xs ">
                    Body Part 1 <span className="text-red-500">*</span>
                  </label>
                  <ReactQuill
                    value={formData.body1 || ""}
                    onChange={(content) =>
                      setFormData((prev) => ({ ...prev, body1: content }))
                    }
                    modules={quillModules}
                    className="bg-white/10 rounded-lg"
                    theme="snow"
                  />
                </div>

                {/* Closing Remarks (renamed from Prepared By) */}
                <div className="mb-4 bg-white/10 p-4 rounded-lg">
                  <h3 className="font-bold text-lg mb-3">Closing Remarks</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="sm:col-span-3">
                      <label htmlFor="complimentary" className="block text-xs">
                        Complimentary Closing
                      </label>
                      <input
                        id="complimentary"
                        type="text"
                        value={
                          formData.closingRemarks?.complimentary ||
                          "Respectfuly,"
                        }
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            closingRemarks: {
                              ...prev.closingRemarks,
                              complimentary: e.target.value,
                            },
                          }))
                        }
                        className="custom-input"
                        placeholder="Enter complementary closing"
                      />
                    </div>
                    <div>
                      <label className="block text-xs ">Name</label>
                      <input
                        type="text"
                        value={formData.closingRemarks?.name || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            closingRemarks: {
                              ...prev.closingRemarks,
                              name: e.target.value,
                            },
                          }))
                        }
                        className="custom-input"
                      />
                    </div>

                    <div>
                      <label className="block text-xs ">Position</label>
                      <input
                        type="text"
                        value={formData.closingRemarks?.position || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            closingRemarks: {
                              ...prev.closingRemarks,
                              position: e.target.value,
                            },
                          }))
                        }
                        className="custom-input"
                      />
                    </div>

                    <div>
                      <label className="block text-xs ">Company</label>
                      <input
                        type="text"
                        value={formData.closingRemarks?.company || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            closingRemarks: {
                              ...prev.closingRemarks,
                              company: e.target.value,
                            },
                          }))
                        }
                        className="custom-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Add this new References section */}
                {/* {logType === "case-brief" && (
                  <div>
                    <label
                      htmlFor="references"
                      className="block text-xs text-color"
                    >
                      References
                    </label>
                    <ReactQuill
                      id="references"
                      value={formData.references || ""}
                      onChange={(value) =>
                        handleInputChange("references", value)
                      }
                      modules={quillModules}
                      placeholder="Enter case references..."
                      theme="snow"
                      className="bg-white/10 rounded-lg"
                    />
                  </div>
                )} */}
              </>
            ) : (
              <>
                {/* Render fields in a grid */}
                <div className="flex flex-col gap-6 pt-4">
                  <div className="flex flex-row gap-2">
                    <div className="flex-1">
                      <label
                        htmlFor="sectask"
                        className="block text-xs text-color"
                      >
                        Task
                      </label>
                      <input
                        id="sectask"
                        value={formData.task}
                        onChange={(e) =>
                          handleInputChange("task", e.target.value)
                        }
                        className={`custom-input`}
                        placeholder={"Enter task..."}
                      />
                    </div>

                    <div className="w-auto">
                      <label
                        htmlFor="dateCompleted"
                        className="block text-xs text-color"
                      >
                        Date Completed
                      </label>
                      <input
                        id="dateCompleted"
                        type="date"
                        value={formData.dateCompleted}
                        onChange={(e) =>
                          handleInputChange("dateCompleted", e.target.value)
                        }
                        className="custom-input"
                        disabled={formData.status !== "Completed"}
                      />
                    </div>
                  </div>

                  {logType === "process-server-logs" && (
                    <div>
                      <label
                        htmlFor="agency"
                        className="block text-xs text-color"
                      >
                        Agency
                      </label>
                      <Select
                        id="agency"
                        value={
                          formData.agency
                            ? {
                                value: formData.agency,
                                label: formData.agency,
                              }
                            : { value: "", label: "Agency..." }
                        }
                        onChange={(selectedOption) => {
                          if (selectedOption.value === "__add_new__") {
                            openModal("agencyList", "Agency");
                          } else {
                            handleInputChange("agency", selectedOption.value);
                          }
                        }}
                        onInputChange={(inputValue, { action }) => {
                          // Only update on actual typing, not on other actions
                          if (action === "input-change" && inputValue) {
                            handleInputChange("agency", inputValue);
                            // Add to dropdown list if it's a new value
                            if (
                              !dropdownLists.agencyList?.includes(inputValue)
                            ) {
                              onAddToDropdown("agencyList", inputValue);
                            }
                          }
                        }}
                        options={getDropdownOptions("agencyList", "Agency...")}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                        isSearchable={true}
                        isClearable={true}
                        formatCreateLabel={(inputValue) =>
                          `Use "${inputValue}"`
                        }
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Status */}
                    <div>
                      <label
                        htmlFor="status"
                        className="block text-xs text-color"
                      >
                        Status
                      </label>
                      <Select
                        id="status"
                        value={{
                          value: formData.status,
                          label: formData.status,
                        }}
                        onChange={(selectedOption) =>
                          handleInputChange("status", selectedOption.value)
                        }
                        options={[
                          { value: "Not Started", label: "Not Started" },
                          { value: "In Progress", label: "In Progress" },
                          { value: "Interrupted", label: "Interrupted" },
                          { value: "Completed", label: "Completed" },
                        ]}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                      />
                    </div>

                    {/* Secretary or Processor */}
                    {logType === "secretarial-records" ? (
                      <div>
                        <label
                          htmlFor="secretary"
                          className="block text-xs text-color"
                        >
                          Secretary Assigned
                        </label>
                        <Select
                          id="secretary"
                          value={
                            formData.secretary
                              ? {
                                  value: formData.secretary,
                                  label: formData.secretary,
                                }
                              : { value: "", label: "Secretary..." }
                          }
                          onChange={(selectedOption) => {
                            if (selectedOption.value === "__add_new__") {
                              openModal("secretaryList", "Secretary Assigned");
                            } else {
                              handleInputChange(
                                "secretary",
                                selectedOption.value
                              );
                            }
                          }}
                          options={getDropdownOptions(
                            "secretaryList",
                            "Secretary..."
                          )}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                        />
                      </div>
                    ) : logType === "process-server-logs" ? (
                      <div>
                        <label
                          htmlFor="processor"
                          className="block text-xs text-color"
                        >
                          Processed By
                        </label>
                        <Select
                          id="processor"
                          value={
                            formData.processedBy
                              ? {
                                  value: formData.processedBy,
                                  label: formData.processedBy,
                                }
                              : { value: "", label: "Processor..." }
                          }
                          onChange={(selectedOption) => {
                            if (selectedOption.value === "__add_new__") {
                              openModal("processedByList", "Processor");
                            } else {
                              handleInputChange(
                                "processedBy",
                                selectedOption.value
                              );
                            }
                          }}
                          options={getDropdownOptions(
                            "processedByList",
                            "Processor..."
                          )}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                        />
                      </div>
                    ) : null}

                    {/* Priority Type */}
                    <div>
                      <label
                        htmlFor="priority"
                        className="block text-xs text-color"
                      >
                        Priority Type
                      </label>
                      <Select
                        id="priority"
                        value={
                          formData.priority
                            ? {
                                value: formData.priority,
                                label: formData.priority,
                              }
                            : null
                        }
                        onChange={(selectedOption) =>
                          handleInputChange("priority", selectedOption.value)
                        }
                        options={[
                          {
                            value: "Important and Urgent",
                            label: "Important and Urgent",
                          },
                          {
                            value: "Important and not Urgent",
                            label: "Important and not Urgent",
                          },
                          {
                            value: "Not Important and Urgent",
                            label: "Not Important and Urgent",
                          },
                          {
                            value: "Not Important and Not Urgent",
                            label: "Not Important and Not Urgent",
                          },
                        ]}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                      />
                    </div>

                    {/* Assisted To / Delegated To */}
                    {logType !== "exec-admin" && logType !== "case-brief" && (
                      <div>
                        <label
                          htmlFor="assistedTo"
                          className="block text-xs text-color"
                        >
                          Assisted To / Delegated To
                        </label>
                        <Select
                          id="assistedTo"
                          value={
                            formData.assistedTo
                              ? {
                                  value: formData.assistedTo,
                                  label: formData.assistedTo,
                                }
                              : { value: "", label: "None" }
                          }
                          onChange={(selectedOption) => {
                            if (selectedOption.value === "__add_new__") {
                              openModal(
                                "assistedList",
                                "Assisted/Delegated To"
                              );
                            } else {
                              handleInputChange(
                                "assistedTo",
                                selectedOption.value
                              );
                            }
                          }}
                          options={getDropdownOptions("assistedList", "None")}
                          styles={selectStyles}
                          classNamePrefix="react-select"
                        />
                      </div>
                    )}
                  </div>

                  {logType === "process-server-logs" && (
                    <>
                      <div>
                        <label
                          htmlFor="toProcess"
                          className="block text-xs text-color"
                        >
                          To Process
                        </label>
                        <ReactQuill
                          id="toProcess"
                          value={formData.toProcess || ""}
                          onChange={(value) =>
                            handleInputChange("toProcess", value)
                          }
                          modules={quillModules}
                          placeholder="To Process..."
                          theme="snow"
                          className="bg-white/10 rounded-lg"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="dependencies"
                          className="block text-xs text-color"
                        >
                          Dependencies
                        </label>
                        <ReactQuill
                          id="dependencies"
                          value={formData.dependencies || ""}
                          onChange={(value) =>
                            handleInputChange("dependencies", value)
                          }
                          modules={quillModules}
                          placeholder="Dependencies..."
                          theme="snow"
                          className="bg-white/10 rounded-lg"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label
                      htmlFor="remarks"
                      className="block text-xs text-color"
                    >
                      Remarks
                    </label>
                    <ReactQuill
                      id="remarks"
                      key={`remarks`}
                      value={formData.remarks || ""}
                      onChange={(value) => handleInputChange("remarks", value)}
                      modules={quillModules}
                      placeholder="Remarks..."
                      theme="snow"
                      className="bg-white/10 rounded-lg"
                    />
                  </div>
                </div>

                <div className="border-b-2 border-tier-secondary pb-4"></div>

                <div className="m-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-md font-bold">Prepared By</h2>
                    <button
                      type="button"
                      onClick={addPreparedBy}
                      className="btn-action text-xs flex items-center gap-1"
                    >
                      <span className="material-symbols-outlined text-sm">
                        add
                      </span>
                      Add Person
                    </button>
                  </div>

                  {!formData.preparedBy || formData.preparedBy.length === 0 ? (
                    <div className="text-center py-8 text-alt-color border border-dashed border-gray-400 rounded">
                      <span className="material-symbols-outlined text-3xl opacity-50">
                        person_add
                      </span>
                      <p className="mt-2 text-sm">No preparers added yet</p>
                      <button
                        type="button"
                        onClick={addPreparedBy}
                        className="btn-action-white text-xs mt-2"
                      >
                        Add First Person
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {formData.preparedBy.map((person, index) => (
                        <div
                          key={index}
                          className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-white/5"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-color">
                              Person {index + 1}
                            </span>
                            {formData.preparedBy.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removePreparedBy(index)}
                                className="text-red-500 hover:bg-red-500/20 p-1 rounded transition-colors"
                                title="Remove person"
                              >
                                <span className="material-symbols-outlined text-sm">
                                  delete
                                </span>
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div>
                              <label
                                htmlFor={`preparedBy-name-${index}`}
                                className="block text-xs mb-1"
                              >
                                Name
                              </label>
                              <input
                                id={`preparedBy-name-${index}`}
                                type="text"
                                value={person.name || ""}
                                onChange={(e) =>
                                  handlePreparedByChange(
                                    index,
                                    "name",
                                    e.target.value
                                  )
                                }
                                className="w-full custom-input"
                                placeholder="Enter name"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`preparedBy-position-${index}`}
                                className="block text-xs mb-1"
                              >
                                Position
                              </label>
                              <input
                                id={`preparedBy-position-${index}`}
                                type="text"
                                value={person.position || ""}
                                onChange={(e) =>
                                  handlePreparedByChange(
                                    index,
                                    "position",
                                    e.target.value
                                  )
                                }
                                className="w-full custom-input"
                                placeholder="Enter position"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`preparedBy-company-${index}`}
                                className="block text-xs mb-1"
                              >
                                Company
                              </label>
                              <input
                                id={`preparedBy-company-${index}`}
                                type="text"
                                value={person.company || ""}
                                onChange={(e) =>
                                  handlePreparedByChange(
                                    index,
                                    "company",
                                    e.target.value
                                  )
                                }
                                className="w-full custom-input"
                                placeholder="Enter company"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </form>

          {/* Info Section */}
          {logData && (
            <div className="mt-6 pt-6 border-t dark:border-gray-600 border-gray-200">
              <div className="flex items-center text-xs text-alt-color">
                <span className="material-symbols-outlined text-sm mr-1">
                  info
                </span>
                <span>
                  Created: {new Date(logData.createdAt).toLocaleString()}
                  {logData.updatedAt && (
                    <>
                      {" "}
                      | Last Updated:{" "}
                      {new Date(logData.updatedAt).toLocaleString()}
                    </>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* comments */}
      <LogComment
        userId={userData?.uid}
        logType={logType}
        logFirebaseId={formData.firebaseId}
        currentUser={userData}
      />

      {showPreviewLog && (
        <PdfPreviewModal
          invoiceData={null}
          pdfUrl={pdfUrl}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

export default LogsForm;
